/**
 * Cadence ↔ admin_bias mapping helpers.
 *
 * The free-tier signup wizard expresses intensity as three named
 * cadence presets (`light`, `balanced`, `intense`). The
 * `intensity_state` Firestore document stores intensity as an integer
 * `admin_bias` in the range [-2, 2] (Lighter ↔ Auto ↔ Fuller). Both
 * surfaces share the same picker UI and write to the same backing
 * field — these helpers are the single point where the two
 * vocabularies are reconciled.
 *
 * Slider positions -1 and +1 (between cards) collapse to the nearest
 * card label so the card picker stays coherent when an admin has
 * fine-tuned past the cards in Settings.
 */

export type Cadence = 'light' | 'balanced' | 'intense'

export function cadenceToAdminBias(cadence: Cadence): -2 | 0 | 2 {
  if (cadence === 'light') return -2
  if (cadence === 'intense') return 2
  return 0
}

export function adminBiasToCadence(bias: number): Cadence {
  if (typeof bias !== 'number' || !Number.isFinite(bias)) return 'balanced'
  if (bias <= -1) return 'light'
  if (bias >= 1) return 'intense'
  return 'balanced'
}

export function isCadence(value: unknown): value is Cadence {
  return value === 'light' || value === 'balanced' || value === 'intense'
}

/**
 * Concrete delivery targets per cadence.
 *
 * The scheduler reads these to decide each day's pack and to enforce
 * the per-week open-ended quota. Source of truth for the numbers shown
 * in the picker copy, the Settings header summary, and the calendar
 * preview.
 *
 * Light and Balanced have hard caps. Intense has no limits: every
 * eligible question fires at its full per-question frequency setting.
 *
 * The existing CES math in intensity.ts (`healthSummary` etc.) is an
 * older heuristic. These targets are the product spec. When the
 * scheduler is built, pack to these targets first and use CES only to
 * break ties or to enforce the open-ended cap inside a single day.
 */
export interface CadenceTargets {
  /** Approximate number of questions per day. null = no cap. */
  questions_per_day: { min: number; max: number } | null
  /** Approximate number of open-ended questions per week. null = no cap. */
  open_ended_per_week: number | null
}

export const CADENCE_TARGETS: Record<Cadence, CadenceTargets> = {
  light:    { questions_per_day: { min: 2, max: 3 }, open_ended_per_week: 1 },
  balanced: { questions_per_day: { min: 3, max: 4 }, open_ended_per_week: 2 },
  intense:  { questions_per_day: null,               open_ended_per_week: null },
}
